// Lightweight Web Audio synthesis engine (no asset downloads).
class AudioEngine {
  constructor() {
    this.ctx = null;
    this.enabled = true;
    this.musicEnabled = true;
    this.master = null;
    this.musicGain = null;
    this.musicNodes = [];
    this._lastEngine = 0;
    this.inMatch = false; // arena music/engine only while a match is actively running
  }
  init() {
    if (this.ctx) return;
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    this.ctx = new AC();
    this.master = this.ctx.createGain();
    this.master.gain.value = 0.5;
    this.master.connect(this.ctx.destination);
    this.musicGain = this.ctx.createGain();
    this.musicGain.gain.value = 0.16;
    this.musicGain.connect(this.master);
  }
  resume() { if (this.ctx && this.ctx.state === "suspended") this.ctx.resume(); }
  setEnabled(v) { this.enabled = v; }
  setMusic(v) {
    this.musicEnabled = v;
    if (v && this.inMatch) this.startMusic(); else this.stopMusic();
  }
  _tone(freq, dur, type = "sine", vol = 0.3, slideTo = null) {
    if (!this.enabled || !this.ctx) return;
    const t = this.ctx.currentTime;
    const o = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    o.type = type;
    o.frequency.setValueAtTime(freq, t);
    if (slideTo) o.frequency.exponentialRampToValueAtTime(slideTo, t + dur);
    g.gain.setValueAtTime(vol, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + dur);
    o.connect(g); g.connect(this.master);
    o.start(t); o.stop(t + dur);
  }
  _noise(dur, vol = 0.4, filterFreq = 1200) {
    if (!this.enabled || !this.ctx) return;
    const t = this.ctx.currentTime;
    const buf = this.ctx.createBuffer(1, this.ctx.sampleRate * dur, this.ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / data.length);
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    const f = this.ctx.createBiquadFilter();
    f.type = "lowpass"; f.frequency.value = filterFreq;
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(vol, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + dur);
    src.connect(f); f.connect(g); g.connect(this.master);
    src.start(t);
  }
  impact(size = 1) { this._noise(0.12 + size * 0.05, 0.35 + size * 0.12, 900 + size * 400); this._tone(120 - size * 20, 0.12, "square", 0.15); }
  crash(size = 1) {
    // Layered metal smash: low thud + mid crunch + high metallic sparkle + debris.
    this._noise(0.24 + size * 0.12, 0.5, 560 + size * 220);
    this._tone(68, 0.3, "sawtooth", 0.24, 32);
    this._tone(150 + size * 45, 0.13, "square", 0.13, 55);
    this._noise(0.09, 0.16 + size * 0.05, 3200 + size * 600);
    if (size >= 1.5) this._tone(230, 0.2, "triangle", 0.11, 90);
  }
  pickup() { this._tone(880, 0.08, "sine", 0.18, 1400); }
  combo(n) {
    const base = 480 + n * 55;
    this._tone(base, 0.09, "triangle", 0.22, base + 260);
    setTimeout(() => this._tone(base * 1.5, 0.08, "triangle", 0.16, base * 1.5 + 200), 55);
  }
  click() { this._tone(660, 0.05, "square", 0.12, 880); }
  levelup() { [523, 659, 784, 1046].forEach((f, i) => setTimeout(() => this._tone(f, 0.18, "triangle", 0.3), i * 70)); }
  evolution() {
    [261, 392, 523, 784, 1046].forEach((f, i) => setTimeout(() => this._tone(f, 0.4, "sawtooth", 0.28), i * 90));
    this._noise(0.6, 0.4, 2000);
  }
  boost() { this._tone(200, 0.3, "sawtooth", 0.2, 600); this._noise(0.3, 0.15, 1500); }
  hit() { this._noise(0.15, 0.4, 500); this._tone(80, 0.2, "square", 0.25, 40); }
  death() { this._tone(300, 0.6, "sawtooth", 0.35, 40); this._noise(0.5, 0.4, 400); }
  victory() { [523, 659, 784, 1046, 1318].forEach((f, i) => setTimeout(() => this._tone(f, 0.3, "triangle", 0.32), i * 120)); }
  engine(speed) {
    if (!this.enabled || !this.ctx) return;
    const now = performance.now();
    if (now - this._lastEngine < 70) return;
    this._lastEngine = now;
    const s = Math.max(0, Math.min(1.4, speed));
    // Deep rumble + gritty harmonic that rises with speed, with a touch of exhaust grit.
    this._tone(46 + s * 26, 0.12, "sawtooth", 0.05 + s * 0.05);
    this._tone(92 + s * 40, 0.09, "square", 0.02 + s * 0.03);
    if (s > 0.6) this._noise(0.06, 0.03 + s * 0.02, 500 + s * 300);
  }
  startMusic() {
    if (!this.musicEnabled || !this.inMatch || !this.ctx || this.musicNodes.length) return;
    const seq = [55, 55, 82, 55, 73, 55, 65, 82];
    let i = 0;
    const step = () => {
      if (!this.musicEnabled || !this.ctx) return;
      const f = seq[i % seq.length];
      const t = this.ctx.currentTime;
      const o = this.ctx.createOscillator();
      const g = this.ctx.createGain();
      o.type = "sawtooth"; o.frequency.value = f;
      g.gain.setValueAtTime(0.14, t);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.32);
      const flt = this.ctx.createBiquadFilter();
      flt.type = "lowpass"; flt.frequency.value = 600;
      o.connect(flt); flt.connect(g); g.connect(this.musicGain);
      o.start(t); o.stop(t + 0.34);
      i++;
      this._musicTimer = setTimeout(step, 340);
    };
    this.musicNodes.push(1);
    step();
  }
  stopMusic() { clearTimeout(this._musicTimer); this.musicNodes = []; }
}
export const Sound = new AudioEngine();
